import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import ParticipantProductTests from "@/components/ParticipantProductTests";
import { useParticipantDetails } from "@/hooks/useParticipants";
import { useExperiments } from "@/hooks/useExperiments";
import BackLink from "@/components/BackLink";
import { useParams } from 'react-router-dom';

const ParticipantDetail: React.FC = () => {
  const { userId } = useParams();
  const { data: participantData, isLoading, error } = useParticipantDetails(userId || '');
  const { data: experiments, isLoading: experimentsLoading } = useExperiments();

  // Filter product tests to only include those from experiments in current organization
  const filteredProductTests = React.useMemo(() => {
    if (!participantData?.productTests || !experiments) {
      return participantData?.productTests || [];
    }

    // Filter product tests by matching experiment id
    return participantData.productTests.filter(test =>
      test.experimentId === experiments.find(experiment => experiment.id === test.experimentId)?.id
    );
  }, [participantData?.productTests, experiments]);

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink to="/dashboard/participants">Back to participants</BackLink>
        <div className="text-center py-6">
          <p className="text-destructive">Error loading participant: {error.message}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || experimentsLoading) {
    return (
      <div className="space-y-6">
        <BackLink to="/dashboard/participants">Back to participants</BackLink>

        <div className="flex items-start justify-between">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        <div className="grid grid-cols-1 gap-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i}>
                      <Skeleton className="h-4 w-20 mb-1" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <Skeleton className="h-4 w-20 mb-1" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!participantData) {
    return (
      <div className="space-y-6">
        <BackLink to="/dashboard/participants">Back to participants</BackLink>
        <div className="text-center py-6">
          <p className="text-muted-foreground">Participant not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink to="/dashboard/participants">Back to participants</BackLink>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Participant Details</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">Export Profile</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Participant Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Age Group</h3>
                  <p>{participantData.ageGroup}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Ethnicity</h3>
                  <p>{participantData.ethnicity}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Gender</h3>
                  <p>{participantData.gender}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Height</h3>
                  <p>{participantData.height}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">BMI Group</h3>
                  <p>{participantData.bmiGroup}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Weight</h3>
                  <p>{participantData.weight}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Tier</h3>
                  <Badge variant="outline" className={
                    participantData.tier === "Influencer" ? "bg-indigo-100 text-indigo-800" :
                    participantData.tier === "Reviewer" ? "bg-green-100 text-green-800" :
                    "bg-gray-100 text-gray-800"
                  }>
                    {participantData.tier}
                  </Badge>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Dietary Preferences</h3>
                <div className="flex flex-wrap gap-2">
                  {participantData.dietaryPreferences.length > 0 ? (
                    participantData.dietaryPreferences.map((pref, index) => (
                      <Badge key={index} variant="secondary">{pref}</Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No dietary preferences specified</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Medical Conditions</h3>
                <div className="flex flex-wrap gap-2">
                  {participantData.medicalConditions.length > 0 ? (
                    participantData.medicalConditions.map((condition, index) => (
                      <Badge key={index} variant="outline" className="bg-red-50 text-red-800 border-red-300">{condition}</Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No medical conditions specified</p>
                  )}
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {participantData.tags.length > 0 ? (
                  participantData.tags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className={tag === 'Diabetic' ? "bg-red-100 text-red-800 border-red-300" : "bg-gray-100"}
                    >
                      {tag}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No tags</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ParticipantProductTests productTests={filteredProductTests} />
    </div>
  );
};

export default ParticipantDetail;